import { Example } from "../models/ExampleModel";
import { ExplanationModel } from "../models/ExplanationModel";
import { ReflexiveModel } from "../models/ReflexiveVerbsModel";
import { TableModel } from "../models/table-models/TableModel";
import { VerbConjugation } from "../models/VerbConjugationModel";

export const reflexiveVerbExplanation: ExplanationModel = {
    title: "📖 What are Verbos Reflexivos?",
    description:
        "Reflexive verbs are used when the subject and the object of the verb are the same person. They are commonly used for actions related to oneself, such as personal care, emotions, or daily routines.",
    bulletPoints: [
        "Reflexive verbs require reflexive pronouns: me, te, se, nos, os, se.",
        "The pronoun 'se' is used for reflexive verbs in all forms except the first and second person singular.",
        "They are often used for describing routines: bañarse (to bathe oneself), vestirse (to get dressed).",
        "Some verbs change meaning when used reflexively: ir (to go) vs. irse (to leave).",
    ],
};

export const reflexiveVsNonReflexive: ReflexiveModel[] = [
    {
        verb: "Meter / Meterse",
        nonReflexiveMeaning: "To put something inside",
        reflexiveMeaning: "To get involved in something",
        nonReflexiveExample: "Meto los libros en la mochila. (I put the books in the backpack.)",
        reflexiveExample: "Me meto en la conversación. (I get involved in the conversation.)",
    },
    {
        verb: "Negar / Negarse",
        nonReflexiveMeaning: "To deny something",
        reflexiveMeaning: "To refuse to do something",
        nonReflexiveExample: "Niega todo lo que dijeron. (He denies everything they said.)",
        reflexiveExample: "Me niego a aceptar eso. (I refuse to accept that.)",
    },
    {
        verb: "Ocupar / Ocuparse",
        nonReflexiveMeaning: "To occupy something",
        reflexiveMeaning: "To take care of something",
        nonReflexiveExample: "Ocupa la silla con su mochila. (He occupies the chair with his backpack.)",
        reflexiveExample: "Me ocupo de los niños. (I take care of the children.)",
    },
    {
        verb: "Perder / Perderse",
        nonReflexiveMeaning: "To lose something",
        reflexiveMeaning: "To get lost",
        nonReflexiveExample: "Perdí mis llaves. (I lost my keys.)",
        reflexiveExample: "Me perdí en la ciudad. (I got lost in the city.)",
    },
    {
        verb: "Retirar / Retirarse",
        nonReflexiveMeaning: "To withdraw something",
        reflexiveMeaning: "To leave or retire",
        nonReflexiveExample: "Retiró el dinero del banco. (He withdrew the money from the bank.)",
        reflexiveExample: "Me retiro temprano de la reunión. (I leave the meeting early.)",
    },
    {
        verb: "Reunir / Reunirse",
        nonReflexiveMeaning: "To gather something",
        reflexiveMeaning: "To meet",
        nonReflexiveExample: "Reunió los documentos importantes. (He gathered the important documents.)",
        reflexiveExample: "Nos reunimos en el café. (We meet at the café.)",
    },
    {
        verb: "Sentar / Sentarse",
        nonReflexiveMeaning: "To seat someone",
        reflexiveMeaning: "To sit down",
        nonReflexiveExample: "Sienta al niño en la silla. (He seats the child in the chair.)",
        reflexiveExample: "Me siento en el sofá. (I sit on the couch.)",
    },
    {
        verb: "Volver / Volverse",
        nonReflexiveMeaning: "To return",
        reflexiveMeaning: "To become something",
        nonReflexiveExample: "Vuelvo a casa temprano. (I return home early.)",
        reflexiveExample: "Se volvió muy serio con el tiempo. (He became very serious over time.)",
    },
    {
        verb: "Poner / Ponerse",
        nonReflexiveMeaning: "To place something",
        reflexiveMeaning: "To put on (clothes) / to become (emotional state)",
        nonReflexiveExample: "Pone el libro en la mesa. (He puts the book on the table.)",
        reflexiveExample: "Me pongo nervioso antes de los exámenes. (I get nervous before exams.)",
    },
];

export const reflexiveVerbsRegular: VerbConjugation[] = [
    new VerbConjugation("Bañarse", ["me baño", "te bañas", "se baña", "nos bañamos", "os bañáis", "se bañan"]),
    new VerbConjugation("Lavarse", ["me lavo", "te lavas", "se lava", "nos lavamos", "os laváis", "se lavan"]),
    new VerbConjugation("Peinarse", ["me peino", "te peinas", "se peina", "nos peinamos", "os peináis", "se peinan"]),
];

export const reflexiveVerbsIrregular : VerbConjugation[] = [
    new VerbConjugation("Despertarse (e → ie)", ["me despierto", "te despiertas", "se despierta", "nos despertamos", "os despertáis", "se despiertan"]),
    new VerbConjugation("Vestirse (e → i)", ["me visto", "te vistes", "se viste", "nos vestimos", "os vestís", "se visten"]),
    new VerbConjugation("Acostarse (o → ue)", ["me acuesto", "te acuestas", "se acuesta", "nos acostamos", "os acostáis", "se acuestan"]),
];


export const reflexivePronouns: TableModel = {
    title: "Reflexive Pronouns",
    headers: ["Subject", "Pronoun"],
    rows: [
        ["Yo", "me"],
        ["Tú", "te"],
        ["Él / Ella / Usted", "se"],
        ["Nosotros / Nosotras", "nos"],
        ["Vosotros / Vosotras", "os"],
        ["Ellos / Ellas / Ustedes", "se"],
    ],
};

export const reflexiveVerbExamples: Example[] = [
    { sentence: "Yo me despierto temprano.", explanation: "I wake up early." },
    { sentence: "Ella se viste antes de salir.", explanation: "She gets dressed before leaving." },
    { sentence: "Nosotros nos lavamos las manos antes de comer.", explanation: "We wash our hands before eating." },
    { sentence: "Ellos se acuestan tarde los fines de semana.", explanation: "They go to bed late on weekends." },
    { sentence: "Te la pones cada mañana.", explanation: "You put it on (the jacket) every morning." },
];
